/** Persistent settlement ground shared by an assault and the town entered afterwards. */
import { createBattle } from '../battle.ts';
import { loadWorld, saveWorld } from '../save.ts';
import { Phase, Team, UF } from '../types.ts';
import { invalidateCompanyIndex, recomputeManaCap, recomputePopCap, recomputeStorage, removeBuilding, type World } from '../world.ts';
import { recount } from '../systems/towers.ts';
import { battleConfigFor } from './conquest.ts';
import { inheritCrown } from './holdworld.ts';
import { raceOfFaction } from '../../data/races.ts';
import { syncVillageWorld } from './villageworld.ts';

/** Clone once at entry. The battle owns its damage; the parked city owns its economy. */
export function createTownBattle(realm: World, town: World): World | null {
  syncVillageWorld(realm, town);
  const config = battleConfigFor(realm);
  if (!config?.town || config.province !== (town.config.province ?? realm.campaign?.capital)) return null;
  const field = createBattle({ ...config, villageType: town.config.villageType }, loadWorld(saveWorld(town)));
  field.villageLevel = town.villageLevel;
  return field;
}

/** Copy only battle damage home; no muster, loot or battle purse belongs to the town. */
export function applyTownBattleDamage(town: World, field: World): void {
  if (!field.battle || (town.config.province !== undefined && field.config.province !== town.config.province)) return;
  const a = town.buildings;
  const b = field.buildings;
  for (let i = 0; i < a.slots.high; i++) {
    if (!a.slots.alive[i]) continue;
    if (!b.slots.alive[i]) removeBuilding(town, i, true);
    else {
      if (b.hp[i] < a.hp[i]) a.lastHitTick[i] = town.tick;
      a.hp[i] = Math.min(a.hp[i], b.hp[i]);
    }
  }
  for (const key of ['wall', 'wallHp', 'wallMaxHp', 'wallTier', 'stain'] as const) {
    town.map[key].set(field.map[key]);
  }
  town.map.wallChanged();
  town.dirty.flow = true;
  town.dirty.jobs = true;
  town.dirty.districts = true;
  recomputePopCap(town);
  recomputeStorage(town);
  recomputeManaCap(town);
}

/** Change the crown while retaining every road, roof, resource node and breach. */
export function reclaimTown(realm: World, town: World, capital: World | null): World {
  const saved = saveWorld(town);
  saved.config = { ...town.config, ...realm.config, seed: town.config.seed,
    biome: town.config.biome, province: town.config.province, villageType: town.config.villageType, homeCity: true };
  saved.raceId = capital?.raceId ?? realm.config.raceId ?? raceOfFaction(realm.config.factionId);
  saved.leaderId = capital?.leaderId ?? realm.config.leaderId ?? '';
  const owned = loadWorld(saved);
  if (capital) inheritCrown(owned, capital);
  owned.wallTeam = Team.Player;
  for (let i = 0; i < owned.buildings.slots.high; i++) {
    if (owned.buildings.slots.alive[i]) owned.buildings.team[i] = Team.Player;
  }
  // Occupiers do not become free recruits. Residents keep their jobs and homes.
  for (let i = 0; i < owned.units.slots.high; i++) {
    if (!owned.units.slots.alive[i]) continue;
    if ((owned.units.flags[i] & UF.Villager) === 0) owned.units.kill(i);
    else owned.units.team[i] = Team.Player;
  }
  owned.companies = [];
  owned.companions = [];
  invalidateCompanyIndex(owned);
  owned.spawnQueue = [];
  owned.incomingComposition = [];
  owned.gameOver = false;
  owned.causeOfDeath = '';
  owned.phase = Phase.Build;
  owned.phaseT = Number.POSITIVE_INFINITY;
  owned.pendingEvent = null;
  owned.battleHold = false;
  // An extinguished hearth must be repairable when the province is retaken.
  if (owned.hearth >= 0 && owned.buildings.isAlive(owned.hearth)) {
    const h = owned.hearth;
    if (owned.buildings.hp[h] <= 0) owned.buildings.hp[h] = owned.buildings.maxHp[h] * 0.2;
  }
  owned.dirty.flow = true;
  owned.dirty.jobs = true;
  recount(owned);
  syncVillageWorld(realm, owned);
  return owned;
}
